import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Spinner, Modal, Form, Row, Col } from 'react-bootstrap';
import TwoFactorSetup from './TwoFactorSetup';

const TwoFactorManager = () => {
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegenerateBackup, setShowRegenerateBackup] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  // Récupérer le statut 2FA
  const fetchTwoFactorStatus = async () => {
    try {
      const response = await fetch('/api/2fa/status', {
        credentials: 'include' // Inclure les cookies automatiquement
      });
      
      if (response.ok) {
        const data = await response.json();
        setTwoFactorStatus(data);
      } else if (response.status === 401 || response.status === 403) {
        console.log('Non authentifié pour 2FA');
        // Ne pas afficher d'erreur, juste pas de données 2FA
      } else {
        console.error('Erreur récupération statut 2FA:', response.status);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du statut 2FA:', err);
    } finally {
      setLoading(false);
    }
  };

  // Désactiver le 2FA
  const handleDisable2FA = async () => {
    setActionLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/2fa/disable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          password: disablePassword,
          token: disableToken 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTwoFactorStatus(data);
        setShowDisable(false);
        setDisablePassword('');
        setDisableToken('');
      } else {
        setError(data.message || 'Erreur lors de la désactivation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setActionLoading(false);
    }
  };

  // Régénérer les codes de récupération
  const handleRegenerateBackupCodes = async () => {
    setActionLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/2fa/regenerate-backup-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ password: regeneratePassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setNewBackupCodes(data.backupCodes);
        await fetchTwoFactorStatus(); // Actualiser le statut
      } else {
        setError(data.message || 'Erreur lors de la régénération');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Chargement...
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-shield-alt me-2"></i>
            Authentification à deux facteurs
          </h5>
          <Badge bg={twoFactorStatus?.enabled ? 'success' : 'secondary'}>
            {twoFactorStatus?.enabled ? 'Activé' : 'Désactivé'}
          </Badge>
        </Card.Header>
        
        <Card.Body>
          {twoFactorStatus?.enabled ? (
            <div>
              <Alert variant="success">
                <i className="fas fa-check-circle me-2"></i>
                Votre compte est protégé par l'authentification à deux facteurs.
              </Alert>
              
              <div className="mb-3">
                <small className="text-muted">
                  <i className="fas fa-clock me-1"></i>
                  Configuré le : {
                    twoFactorStatus.setupDate 
                      ? new Date(twoFactorStatus.setupDate).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'Date inconnue'
                  }
                </small>
                {twoFactorStatus.lastUsed && (
                  <div>
                    <small className="text-muted">
                      <i className="fas fa-sign-in-alt me-1"></i>
                      Dernière utilisation : {new Date(twoFactorStatus.lastUsed).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </small>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <small className="text-muted d-block mb-2">
                  <i className="fas fa-key me-1"></i>
                  Codes de récupération restants : {twoFactorStatus.backupCodesCount ?? 0}
                </small>
                
                {(twoFactorStatus.backupCodesCount ?? 0) <= 2 && (
                  <Alert variant="warning" className="py-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Il vous reste peu de codes de récupération. Pensez à en régénérer.
                  </Alert>
                )}
              </div>
              
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowRegenerateBackup(true)}
                >
                  <i className="fas fa-sync-alt me-2"></i>
                  Régénérer les codes de récupération
                </Button>
                
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowDisable(true)}
                >
                  <i className="fas fa-times me-2"></i>
                  Désactiver le 2FA
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
              </Alert>
              
              <p className="text-muted">
                Avec le 2FA activé, vous devrez entrer un code depuis votre téléphone en plus de votre mot de passe pour vous connecter.
              </p>
              
              <Button
                variant="primary"
                onClick={() => setShowSetup(true)}
              >
                <i className="fas fa-shield-alt me-2"></i>
                Activer l'authentification à deux facteurs
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de configuration 2FA */}
      <TwoFactorSetup
        show={showSetup}
        onHide={() => setShowSetup(false)}
        onSetupComplete={() => {
          fetchTwoFactorStatus();
          setShowSetup(false);
        }}
      />

      {/* Modal de désactivation */}
      <Modal show={showDisable} onHide={() => {
        setShowDisable(false);
        setDisablePassword('');
        setDisableToken('');
        setError('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
            Désactiver l'authentification à deux facteurs
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}
          
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Êtes-vous sûr de vouloir désactiver l'authentification à deux facteurs ? 
            Cela rendra votre compte moins sécurisé.
          </Alert>
          
          <Form.Group className="mb-3">
            <Form.Label>Confirmez votre mot de passe</Form.Label>
            <Form.Control
              type="password"
              placeholder="Entrez votre mot de passe"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </Form.Group>
          
          <Form.Group>
            <Form.Label>Code d'authentification 2FA</Form.Label>
            <Form.Control
              type="text"
              placeholder="000000"
              value={disableToken}
              onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').substring(0, 6))}
              maxLength={6}
            />
            <Form.Text className="text-muted">
              Entrez le code à 6 chiffres de votre application d'authentification
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDisable(false);
            setDisablePassword('');
            setDisableToken('');
            setError('');
          }}>
            Annuler
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDisable2FA}
            disabled={!disablePassword || !disableToken || disableToken.length !== 6 || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : 'Désactiver le 2FA'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de régénération des codes de récupération */}
      <Modal show={showRegenerateBackup} onHide={() => {
        setShowRegenerateBackup(false);
        setNewBackupCodes([]);
        setRegeneratePassword('');
        setError('');
      }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-sync-alt me-2"></i>
            Régénérer les codes de récupération
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}
          
          {newBackupCodes.length === 0 ? (
            <div>
              <Alert variant="warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Cette action va remplacer tous vos codes de récupération existants. 
                Les anciens codes ne fonctionneront plus.
              </Alert>
              
              <Form.Group>
                <Form.Label>Confirmez votre mot de passe</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Entrez votre mot de passe"
                  value={regeneratePassword}
                  onChange={(e) => setRegeneratePassword(e.target.value)}
                />
              </Form.Group>
            </div>
          ) : (
            <div>
              <Alert variant="success">
                <i className="fas fa-check-circle me-2"></i>
                Nouveaux codes de récupération générés avec succès !
              </Alert>
              
              <p className="text-muted">
                Sauvegardez ces nouveaux codes dans un endroit sûr. Les anciens codes ne fonctionnent plus.
              </p>
              
              <div className="bg-light p-3 rounded border">
                <Row>
                  {newBackupCodes.map((code, index) => (
                    <Col md={6} key={index} className="mb-2">
                      <code className="d-block p-2 bg-white border rounded text-center">
                        {code}
                      </code>
                    </Col>
                  ))}
                </Row>
              </div>
              
              <div className="mt-3 d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const codesText = newBackupCodes.join('\n');
                    navigator.clipboard.writeText(codesText);
                  }}
                >
                  <i className="fas fa-copy me-2"></i>
                  Copier
                </Button>
                
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const dataStr = `data:text/plain;charset=utf-8,${encodeURIComponent(newBackupCodes.join('\n'))}`;
                    const downloadAnchorNode = document.createElement('a');
                    downloadAnchorNode.setAttribute('href', dataStr);
                    downloadAnchorNode.setAttribute('download', 'nouveaux-codes-recuperation-2fa.txt');
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                  }}
                >
                  <i className="fas fa-download me-2"></i>
                  Télécharger
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowRegenerateBackup(false);
            setNewBackupCodes([]);
            setRegeneratePassword('');
            setError('');
          }}>
            {newBackupCodes.length > 0 ? 'Fermer' : 'Annuler'}
          </Button>
          
          {newBackupCodes.length === 0 && (
            <Button 
              variant="warning" 
              onClick={handleRegenerateBackupCodes}
              disabled={!regeneratePassword || actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : 'Régénérer les codes'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TwoFactorManager;
